/**
 * SessionLog Component
 * 
 * Displays a chronological timeline of all session annotations including flags, events,
 * phases, and connection status. Provides interactive features for reviewing and managing
 * annotations during proctoring session playback.
 * 
 * Features:
 * - Timeline visualization of annotations
 * - Auto-scroll to follow current playback position
 * - Expandable metadata viewer for detailed annotation information
 * - Color-coded annotations based on severity/type
 * - Filter for unreviewed annotations
 * - Jump to annotation timestamp on click
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import './styles.css';
import {
    View,
    Alert,
    IconVideoCameraSolid,
    IconButton,
    Checkbox,
    IconSyllabusLine,
    IconXLine,
    Flex,
    Pill,
    Text,
    Link,
    SourceCodeEditor,
} from '@smarterservices/smarter-ui';
import Timeline from '../../Common/Timeline';
import Loader from '@ui/context/session/components/Common/Loader';
import { useExamSessionStore, usePlayerStore, getStore } from '../../../stores/StoreContext';
import { formatTime, formatDuration } from '../../../helpers/utilities';
import CONSTANTS from '../../../config/constants';
import { NavigationalToolTip } from '@ui/shared/components/Common/NavigationalToolTip';

/**
 * Title Component
 * 
 * Renders the title section of an annotation timeline item, including:
 * - Annotation type name
 * - Duration pill (if applicable)
 * - Metadata toggle button (if metadata exists)
 * 
 * @param {Object} annotation - The annotation object containing type, duration, and range info
 * @param {boolean} isExpanded - Whether the metadata is currently expanded
 * @param {Function} onToggle - Callback to toggle metadata visibility (receives annotation.sid)
 * @param {boolean} hasMetadata - Whether the annotation has metadata to display
 */
const Title = React.memo(({ annotation, isExpanded, onToggle, hasMetadata }) => {
    let duration;
    let pillColor = "primary";
    if (annotation.duration) {
        duration = formatDuration(annotation.duration);
    }

    if (!annotation.duration && annotation.range.start && !annotation.range.end) {
        duration = 'Ongoing';
        pillColor = "danger";
    }

    const handleToggle = () => onToggle(annotation.sid);

    return (
        <Flex gap="x-small" alignItems="center">
            <Flex.Item>
                <span>{annotation.type.name}</span>
            </Flex.Item>
            {duration && (
                <Flex.Item>
                    <Pill color={pillColor} margin="none">
                        <Text variant="legend">{duration}</Text>
                    </Pill>
                </Flex.Item>
            )}
            {hasMetadata && (
                <Flex.Item>
                    <IconButton
                        size="small"
                        withBackground={false}
                        withBorder={false}
                        onClick={handleToggle}
                        screenReaderLabel={isExpanded ? "Hide metadata" : "Show metadata"}
                    >
                        {isExpanded ? <IconXLine /> : <IconSyllabusLine />}
                    </IconButton>
                </Flex.Item>
            )}
        </Flex>
    );
});

/**
 * SessionLog Component
 * Main component for displaying and managing session annotations
 */
const SessionLog = () => {
    // Constants
    const menuHeight = 55;
    const types = null; // Filter for annotation types (null = show all)

    // State: Data Management
    const [markers, setMarkers] = useState({}); // Marker definitions by ID
    const [timelineData, setTimelineData] = useState([]); // Processed timeline items
    const [expandedMetadata, setExpandedMetadata] = useState({}); // Track which annotations have expanded metadata
    const [initialized, setInitialized] = useState(false); // Track if initial data load is complete

    // State: UI Controls
    const [showUnreviewed, setShowUnreviewed] = useState(false); // Filter to show only unreviewed annotations
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true); // Auto-scroll with playback
    const [autoscrollTopOffset, setAutoscrollTopOffset] = useState(menuHeight); // Offset for auto-scroll positioning

    // Refs
    const timelineRef = useRef(); // Reference to Timeline component
    const controlsRef = useRef(); // Reference to controls container

    // Store Access
    const ExamSessionStore = getStore('examSessionStore');
    const PlayerStore = getStore('playerStore');

    // Store State Subscriptions
    const annotations = useExamSessionStore((state) => state.annotations); // All session annotations
    const displayMarkers = usePlayerStore((state) => state.displayMarkers); // Active marker filters
    const currentTime = usePlayerStore((state) => state.currentTime); // Current playback time

    /**
     * Initialize markers on component mount
     * Converts marker array to object keyed by marker ID for faster lookups
     */
    useEffect(() => {
        const markers = PlayerStore.markers;
        const markersById = {};
        for (const marker of markers) {
            markersById[marker.id] = marker;
        }
        setMarkers(markersById);
    }, [PlayerStore]);

    /**
     * Toggle metadata visibility for a specific annotation
     * @param {string} annotationId - The annotation ID to toggle
     */
    const toggleMetadata = useCallback((annotationId) => {
        setExpandedMetadata(prev => ({
            ...prev,
            [annotationId]: !prev[annotationId]
        }));
    }, []);

    /**
     * Memoized timeline item for recording start marker
     * Displayed when annotations exist before recording started (negative timestamps)
     */
    const recordingStartedOnTimeline = useMemo(() => ({
        icon: {
            icon: <IconVideoCameraSolid />,
            color: 'primary',
            shape: 'rectangle',
            onClick: () => PlayerStore.jumpTo(0),
            onContextMenu: () => { },
        },
        label: <View as="div" textAlign="end" width="65px">{formatTime(0)}</View>,
        title: "Recording Started",
        time: 0,
        detail: <View>Recording Started</View>,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    /**
     * Memoized callback for getting annotation color
     */
    const getAnnotationColor = useCallback((annotation) => {
        if (annotation.categoryId === "events") return "secondary";
        if (annotation.categoryId === "connection") return "danger";
        if (annotation.categoryId === "phases") return "primary";
        if (annotation.categoryId === "flags") return "danger";
        return "primary";
    }, []);

    /**
     * Helper to calculate jump-to timestamp with leading offset
     */
    const calculateJumpToTime = useCallback((startTime) => {
        return startTime > CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
            ? startTime - CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
            : 0;
    }, []);

    /**
     * Memoized handler for jumping to annotation
     */
    const handleJumpToAnnotation = useCallback((startTime) => {
        PlayerStore.jumpTo(calculateJumpToTime(startTime));
    }, [PlayerStore, calculateJumpToTime]);

    /**
     * Memoized empty context menu handler
     */
    const emptyContextMenuHandler = useCallback(() => { }, []);

    /**
     * Memoized callback for getting annotation icon
     */
    const getIcon = useCallback((annotation) => {
        if (annotation.categoryId === "flags") {
            const severity = PlayerStore.flagScoreToSeverity(annotation.score);
            const themeOverride = {
                [`${severity.color}Background`]: severity.backgroundColor,
                [`${severity.color}Color`]: severity.iconColor
            };

            return <IconButton
                as="button"
                cursor="pointer"
                interaction="enabled"
                shape="circle"
                screenReaderLabel="Jump to annotation start"
                margin="xxx-small"
                color={severity.color}
                size="small"
                className='timeline-icon-button'
                onClick={() => handleJumpToAnnotation(annotation.start)}
                themeOverride={themeOverride}
            >
                {severity.icon}
            </IconButton>
        } else {
            const color = getAnnotationColor(annotation);

            return {
                icon: markers[annotation.categoryId]?.icon,
                color: color,
                shape: 'circle',
                onClick: () => handleJumpToAnnotation(annotation.start),
                onContextMenu: emptyContextMenuHandler,
            };
        }
    }, [PlayerStore, markers, getAnnotationColor, handleJumpToAnnotation, emptyContextMenuHandler]);



    /**
     * Build timeline data from annotations
     * Processes annotations and converts them to timeline items with proper formatting,
     * colors, and interactive handlers
     */
    useEffect(() => {
        if (!Object.keys(markers).length) return;
        const tempTimelineData = [];
        let allAnnotations = ExamSessionStore.getAnnotations();
        let recordingStartedMarker = false;

        // Filter for unreviewed annotations if enabled
        if (showUnreviewed) {
            allAnnotations = allAnnotations.filter((annotation) => !annotation.reviewedDate);
        }

        for (const annotation of allAnnotations) {

            // Add recording started marker if annotation is before recording
            if (annotation.start < 0 && !recordingStartedMarker) {
                tempTimelineData.push(recordingStartedOnTimeline);
                recordingStartedMarker = true;
            }

            const annotationOnTimeline = {
                id: annotation.sid,
                icon: getIcon(annotation),
                label:
                    <Flex as="div" justifyItems="end" width="65px">
                        <Flex.Item shouldGrow textAlign="end">
                            {annotation.start < 0 ? "" : formatTime(annotation.start)}
                        </Flex.Item>
                    </Flex>,
                title: (
                    <Title
                        key={annotation.sid}
                        annotation={annotation}
                        isExpanded={expandedMetadata[annotation.sid]}
                        onToggle={toggleMetadata}
                        hasMetadata={!!annotation.metadata && Object.keys(annotation.metadata).length > 0}
                    />
                ),
                time: annotation.start,
                detail: (
                    <View>
                        {annotation.details}
                        {/* Expandable metadata section */}
                        {annotation.metadata && (
                            <View margin="small 0 0 0">
                                {expandedMetadata[annotation.sid] && (
                                    <View as="div" margin="small 0 0 0">
                                        <Text as='p' variant="contentImportant">Metadata</Text>
                                        <View
                                            background='primary'
                                            className="annotation-metadata-wrapper"
                                            borderColor="primary"
                                            borderWidth="small"
                                            borderRadius="medium"
                                            as="div"
                                            maxHeight="300px"
                                            overflowY="auto"
                                            margin="0 small 0 0"
                                        >
                                            <SourceCodeEditor
                                                key={`metadata-${annotation.sid}`}
                                                label="Metadata"
                                                value={JSON.stringify(annotation.metadata, null, 2)}
                                                language="json"
                                                lineNumbers
                                                readOnly
                                                containerRef={(ref) => {
                                                    if (ref) {
                                                        ref.style.marginBottom = "0";
                                                    }
                                                }}
                                                themeOverride={{
                                                    borderWidth: 0,
                                                }}
                                            />
                                        </View>
                                    </View>
                                )}
                            </View>
                        )}
                    </View>
                ),
            };
            tempTimelineData.push(annotationOnTimeline);
        }
        setTimelineData(tempTimelineData);
        setInitialized(true);
    }, [annotations, displayMarkers, markers, toggleMetadata, expandedMetadata, ExamSessionStore, recordingStartedOnTimeline, showUnreviewed, types, getIcon]);

    /**
     * Memoized callback for auto-scroll toggle
     */
    const handleAutoScrollToggle = useCallback(() => {
        setAutoScrollEnabled(prev => !prev);
    }, []);

    // Show loader while initializing
    if (!initialized) {
        return (
            <View as="div" margin="medium 0 0 0">
                <Loader
                    spinnerSize="small"
                    color="primary"
                    textSize="medium"
                    text='Loading session log'
                />
            </View>
        );
    }

    return (
        <View as="div" position="relative">
            {/* Display timeline if annotations exist */}
            {timelineData.length ? (
                <>
                    {/* Controls bar */}
                    <View
                        as="div"
                        className="auto-scroll-toggle"
                        background="primary"
                        elementRef={(ref) => {
                            controlsRef.current = ref;
                        }}
                    >
                        <View
                            as="div"
                            background="secondary"
                            borderColor="primary"
                            borderRadius="none"
                            borderWidth="none none small none"
                            padding="x-small"
                            margin="none none xx-small none"
                        >
                            <Flex justifyItems="space-between" alignItems="center">
                                <Flex.Item />
                                <Flex.Item>
                                    {/* Auto-scroll toggle */}
                                    <View padding="none none none x-small">
                                        <NavigationalToolTip
                                            description="Auto scroll"
                                            placement="bottom start"
                                            shortcut={CONSTANTS?.KEYBOARD_SHORTCUTS?.ANNOTATIONS?.OPTIONS?.AUTO_SCROLL?.KEY}
                                        >
                                            <Checkbox
                                                label="Auto Scroll"
                                                variant="toggle"
                                                size="small"
                                                inline={true}
                                                labelPlacement="end"
                                                checked={autoScrollEnabled}
                                                onChange={handleAutoScrollToggle}
                                                margin="none none none x-small"
                                            />
                                        </NavigationalToolTip>
                                    </View>
                                </Flex.Item>
                            </Flex>
                        </View>

                    </View>
                    {/* Timeline component */}
                    <Timeline
                        ref={timelineRef}
                        items={timelineData}
                        currentTime={currentTime}
                        autoScroll={autoScrollEnabled}
                        showAutoScrollToggle={false}
                        topOffset={autoscrollTopOffset}
                        highlightScrollTarget={true}
                    />
                </>
            ) : (
                /* Empty state messages */
                <>
                    {showUnreviewed ? (
                        <Alert variant="info" transition="none">
                            There are no unreviewed logs associated with this session.
                            <Link onClick={() => setShowUnreviewed(!showUnreviewed)}>Remove the filter</Link> to show all annotations.
                        </Alert>
                    ) : (
                        <Alert variant="success" transition="none" margin="medium">
                            There are no logs associated with this session.
                        </Alert>
                    )}
                </>
            )}
        </View>
    );
};

export default SessionLog;
